import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Brain, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

import { API_BASE_URL as API } from '../utils/apiConfig';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error('No session_id found in URL hash');
        localStorage.removeItem('auth_pending');
        navigate('/');
        return;
      }

      const sessionId = sessionIdMatch[1];
      console.log('Processing auth with session_id:', sessionId.substring(0, 10) + '...');

      const attemptAuth = async (attempt) => {
        try {
          const response = await axios.post(`${API}/api/auth/session`, {
            session_id: sessionId,
          }, {
            withCredentials: true,
            timeout: 15000, // 15 second timeout
          });

          console.log('Auth successful:', response.data.email);
          localStorage.removeItem('auth_pending');
          login(response.data);
          
          // Small delay to ensure cookie is set
          await new Promise(resolve => setTimeout(resolve, 100));
          
          navigate('/dashboard', { state: { user: response.data }, replace: true });
        } catch (error) {
          console.error(`Auth attempt ${attempt + 1} failed:`, error.response?.data || error.message);
          
          if (attempt < maxRetries - 1) {
            // Wait before retry with exponential backoff
            const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
            console.log(`Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            setRetryCount(attempt + 1);
            return attemptAuth(attempt + 1);
          }
          
          // All retries failed
          const errorMessage = error.response?.data?.detail || error.message || 'Authentication failed';
          setError(errorMessage);
          localStorage.removeItem('auth_pending');
        }
      };

      await attemptAuth(0);
    };

    processAuth();
  }, [location, navigate, login]);

  const handleRetry = () => {
    // Clear state and try again
    hasProcessed.current = false;
    setError(null);
    setRetryCount(0);
    window.location.reload();
  };

  const handleGoHome = () => {
    localStorage.removeItem('auth_pending');
    navigate('/');
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="auth-error">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Authentication Failed</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={handleGoHome}>
              Go Home
            </Button>
            <Button onClick={handleRetry} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <Brain className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">
          {retryCount > 0 ? `Retrying... (${retryCount}/${maxRetries})` : 'Signing you in...'}
        </p>
      </div>
    </div>
  );
}
